import React, { useEffect, useRef } from 'react';

interface ParametricSphereArtProps {
  width?: number;
  height?: number;
  dotSpacing?: number;
  rotationSpeed?: number;
  dotColor?: string;
  axisColor?: string;
  opacity?: number;
}

export const ParametricSphereArt: React.FC<ParametricSphereArtProps> = ({
  width = 400,
  height = 400,
  dotSpacing = 2.5,
  rotationSpeed = 0.003,
  dotColor = '#ffffff',
  axisColor = '#ffffff',
  opacity = 0.15
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const timeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log('❌ ParametricSphereArt: Canvas ref is null');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.log('❌ ParametricSphereArt: Could not get 2D context');
      return;
    }

    console.log('✅ ParametricSphereArt: Canvas initialized', { width, height, opacity });
    canvas.style.opacity = opacity.toString();

    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.3;

    const rotatePoint = (p: { x: number; y: number; z: number }, time: number): { x: number; y: number; z: number } => {
      // Y axis stays fixed (vertical), X and Z axes spin around it
      const cosY = Math.cos(time);
      const sinY = Math.sin(time);

      const x1 = p.x * cosY - p.z * sinY;
      const z1 = p.x * sinY + p.z * cosY;

      return { x: x1, y: p.y, z: z1 };
    };

    const projectPoint = (p: { x: number; y: number; z: number }): { x: number; y: number } => {
      // Isometric-like perspective: looking at 45 degrees from the side
      // This creates a clear view of X/Z rotation around Y axis
      const perspectiveScale = 1 + p.z / (radius * 3);
      const isoX = (p.x - p.z) * 0.866; // cos(30°) for isometric
      const isoY = p.y - (p.x + p.z) * 0.5; // sin(30°) for isometric

      return {
        x: centerX + isoX * perspectiveScale,
        y: centerY + isoY * perspectiveScale
      };
    };

    const drawDottedLine = (points: Array<{ x: number; y: number; z: number }>, time: number): void => {
      const rotatedPoints = points.map(p => rotatePoint(p, time));

      const sortedPoints = rotatedPoints
        .map((p, i) => ({ ...p, index: i }))
        .sort((a, b) => a.z - b.z);

      sortedPoints.forEach(p => {
        const screenPos = projectPoint(p);

        ctx.fillStyle = dotColor;
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const drawAxis = (startPoint: { x: number; y: number; z: number }, endPoint: { x: number; y: number; z: number }, time: number, opacity: number = 0.6): void => {
      const rotatedStart = rotatePoint(startPoint, time);
      const rotatedEnd = rotatePoint(endPoint, time);

      const projStart = projectPoint(rotatedStart);
      const projEnd = projectPoint(rotatedEnd);

      ctx.strokeStyle = axisColor;
      ctx.globalAlpha = opacity;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(projStart.x, projStart.y);
      ctx.lineTo(projEnd.x, projEnd.y);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);
    };

    const generateLatitudeLines = (): Array<Array<{ x: number; y: number; z: number }>> => {
      const lines: Array<Array<{ x: number; y: number; z: number }>> = [];
      const numLatitudes = 9;
      const pointsPerLine = 100;

      for (let lat = 0; lat < numLatitudes; lat++) {
        const latitude = (lat / (numLatitudes - 1)) * Math.PI - Math.PI / 2;
        const latRadius = radius * Math.cos(latitude);
        const y = radius * Math.sin(latitude);

        const line: Array<{ x: number; y: number; z: number }> = [];
        for (let i = 0; i < pointsPerLine; i++) {
          const longitude = (i / pointsPerLine) * Math.PI * 2;
          const x = latRadius * Math.cos(longitude);
          const z = latRadius * Math.sin(longitude);

          line.push({ x, y, z });
        }
        lines.push(line);
      }

      return lines;
    };

    const generateLongitudeLines = (): Array<Array<{ x: number; y: number; z: number }>> => {
      const lines: Array<Array<{ x: number; y: number; z: number }>> = [];
      const numLongitudes = 12;
      const pointsPerLine = 80;

      for (let lon = 0; lon < numLongitudes; lon++) {
        const longitude = (lon / numLongitudes) * Math.PI * 2;
        const line: Array<{ x: number; y: number; z: number }> = [];

        for (let i = 0; i < pointsPerLine; i++) {
          const latitude = (i / (pointsPerLine - 1)) * Math.PI - Math.PI / 2;
          const latRadius = radius * Math.cos(latitude);
          const y = radius * Math.sin(latitude);
          const x = latRadius * Math.cos(longitude);
          const z = latRadius * Math.sin(longitude);

          line.push({ x, y, z });
        }
        lines.push(line);
      }

      return lines;
    };

    const generateLissajousCurves = (time: number): Array<Array<{ x: number; y: number; z: number }>> => {
      const curves: Array<Array<{ x: number; y: number; z: number }>> = [];
      const configs = [
        { a: 3, b: 2, delta: 0 },
        { a: 5, b: 4, delta: Math.PI / 2 },
        { a: 4, b: 3, delta: Math.PI / 3 },
        { a: 7, b: 5, delta: Math.PI / 4 },
        { a: 6, b: 4, delta: Math.PI / 6 }
      ];
      const pointsPerCurve = 150;

      configs.forEach((config, idx) => {
        const curve: Array<{ x: number; y: number; z: number }> = [];
        for (let i = 0; i < pointsPerCurve; i++) {
          const t = (i / pointsPerCurve) * Math.PI * 2;
          // Animate the phase shift (faster)
          const animatedDelta = config.delta + time * (0.4 + idx * 0.08);
          const x = radius * Math.sin(config.a * t + animatedDelta);
          const y = radius * Math.sin(config.b * t + time * 0.3);
          const z = radius * 0.3 * Math.cos((config.a + config.b) * t * 0.5 + time * 0.2);

          curve.push({ x, y, z });
        }
        curves.push(curve);
      });

      return curves;
    };

    const generateOrbitalPaths = (time: number): Array<Array<{ x: number; y: number; z: number }>> => {
      const curves: Array<Array<{ x: number; y: number; z: number }>> = [];
      const configs = [
        { inclination: Math.PI / 6, eccentricity: 0.2 },
        { inclination: Math.PI / 3, eccentricity: 0.15 },
        { inclination: Math.PI / 2.5, eccentricity: 0.1 },
        { inclination: Math.PI / 4, eccentricity: 0.25 },
        { inclination: Math.PI / 2, eccentricity: 0.12 }
      ];
      const pointsPerCurve = 120;

      configs.forEach((config, idx) => {
        const curve: Array<{ x: number; y: number; z: number }> = [];
        for (let i = 0; i < pointsPerCurve; i++) {
          const t = (i / pointsPerCurve) * Math.PI * 2;
          // Animate eccentricity pulsing (faster and larger amplitude)
          const pulseEcc = config.eccentricity + Math.sin(time * 0.6 + idx * 0.3) * 0.08;
          const r = radius * (1 - pulseEcc * Math.cos(t));
          const x = r * Math.cos(t);
          const y = r * Math.sin(t) * Math.cos(config.inclination);
          const z = r * Math.sin(t) * Math.sin(config.inclination);

          curve.push({ x, y, z });
        }
        curves.push(curve);
      });

      return curves;
    };

    const latitudeLines = generateLatitudeLines();
    const longitudeLines = generateLongitudeLines();

    const animate = () => {
      timeRef.current += rotationSpeed;
      ctx.clearRect(0, 0, width, height);

      // Generate animated curves each frame
      const lissajousCurves = generateLissajousCurves(timeRef.current);
      const orbitalPaths = generateOrbitalPaths(timeRef.current);

      // Draw latitude lines (horizontal bands - don't rotate)
      latitudeLines.forEach(line => {
        drawDottedLine(line, timeRef.current);
      });

      // Draw longitude lines (vertical meridians - rotate with globe)
      longitudeLines.forEach(line => {
        drawDottedLine(line, timeRef.current);
      });

      // Draw Lissajous curves
      lissajousCurves.forEach(curve => {
        drawDottedLine(curve, timeRef.current);
      });

      // Draw orbital paths
      orbitalPaths.forEach(path => {
        drawDottedLine(path, timeRef.current);
      });

      // Draw axis lines
      const axisLength = radius * 1.5;
      drawAxis({ x: -axisLength, y: 0, z: 0 }, { x: axisLength, y: 0, z: 0 }, timeRef.current, 0.35);
      drawAxis({ x: 0, y: -axisLength, z: 0 }, { x: 0, y: axisLength, z: 0 }, timeRef.current, 0.5);
      drawAxis({ x: 0, y: 0, z: -axisLength }, { x: 0, y: 0, z: axisLength }, timeRef.current, 0.35);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [width, height, dotSpacing, rotationSpeed, dotColor, axisColor, opacity]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="font-seurat"
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        maxWidth: '100vw',
        maxHeight: '100vh',
      }}
    />
  );
};
